// src/App.js
import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom'
import { fetchPortfolio, computePortfolio } from './lib/supabase'
import IDStock   from './pages/IDStock'
import Calculator from './pages/Calculator'
import { BarChart2, Calculator as CalcIcon, TrendingUp, RefreshCw } from 'lucide-react'
import './App.css'

export default function App() {
  const [portfolioData, setPortfolioData] = useState(null)
  const [loading, setLoading]             = useState(true)
  const [error, setError]                 = useState(null)
  const [lastRefresh, setLastRefresh]     = useState(new Date())

  const load = async () => {
    setLoading(true); setError(null)
    try {
      const { stocks, latestPrice, cash } = await fetchPortfolio()
      setPortfolioData(computePortfolio(stocks, latestPrice, cash))
      setLastRefresh(new Date())
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const fmt = n => n ? `Rp ${(n/1_000_000).toFixed(1)} jt` : '—'
  const pct = n => n ? `${(n*100).toFixed(1)}%` : '—'

  return (
    <BrowserRouter>
      <div className="app">

        {/* ── TOP NAV ── */}
        <header className="topnav">
          <div className="topnav-inner">
            <div className="brand">
              <span className="brand-icon"><TrendingUp size={18}/></span>
              <span className="brand-text">Portfolio<strong>RK</strong></span>
            </div>

            <nav className="nav-links">
              <NavLink to="/" end className={({isActive}) => isActive ? 'navlink active' : 'navlink'}>
                <BarChart2 size={15}/> ID Stock
              </NavLink>
              <NavLink to="/calculator" className={({isActive}) => isActive ? 'navlink active' : 'navlink'}>
                <CalcIcon size={15}/> Kalkulator
              </NavLink>
            </nav>

            <button className="refresh-btn" onClick={load} disabled={loading} title="Refresh data">
              <RefreshCw size={14} className={loading ? 'spin' : ''}/>
              {loading ? 'Memuat...' : `Update ${lastRefresh.toLocaleTimeString('id-ID',{hour:'2-digit',minute:'2-digit'})}`}
            </button>
          </div>
        </header>

        {/* ── SUMMARY BAR ── */}
        {portfolioData && !loading && (
          <div className="summary-bar">
            <div className="summary-bar-inner">
              <SumItem label="Total Ekuitas"     value={fmt(portfolioData.totalEquity)}   accent />
              <SumItem label="Nilai Pasar"        value={fmt(portfolioData.totalMktVal)} />
              <SumItem label="Nilai Modal"        value={fmt(portfolioData.totalCostVal)} />
              <SumItem label="Cash Investable"    value={fmt(portfolioData.cashInvestable)} />
              <SumItem label="RDPT"               value={fmt(portfolioData.rdpt)} small />
              <SumItem label="RDN"                value={fmt(portfolioData.rdn)} small />
              <GainBadge
                mkt={portfolioData.totalMktVal}
                cost={portfolioData.totalCostVal}
              />
            </div>
          </div>
        )}

        {/* ── MAIN CONTENT ── */}
        <main className="main">
          {error && (
            <div className="error-banner">
              <strong>Koneksi gagal:</strong> {error}
              <br/><small>Pastikan SUPABASE_URL dan SUPABASE_ANON_KEY sudah diset di .env</small>
            </div>
          )}

          {loading && !portfolioData && (
            <div className="loading-screen">
              <div className="loader-ring"/>
              <p>Memuat data portfolio...</p>
            </div>
          )}

          <Routes>
            <Route path="/" element={
              <IDStock portfolioData={portfolioData} onRefresh={load} loading={loading}/>
            }/>
            <Route path="/calculator" element={
              <Calculator portfolioData={portfolioData} loading={loading}/>
            }/>
          </Routes>
        </main>

      </div>
    </BrowserRouter>
  )
}

function SumItem({ label, value, accent, small }) {
  return (
    <div className={`sum-item ${accent ? 'sum-accent' : ''} ${small ? 'sum-small' : ''}`}>
      <span className="sum-label">{label}</span>
      <span className="sum-value">{value}</span>
    </div>
  )
}

function GainBadge({ mkt, cost }) {
  if (!mkt || !cost) return null
  const gain = (mkt - cost) / cost
  const pos  = gain >= 0
  return (
    <div className={`gain-badge ${pos ? 'pos' : 'neg'}`}>
      <span className="gain-pct">{pos ? '+' : ''}{(gain*100).toFixed(1)}%</span>
      <span className="gain-label">Floating P/L</span>
    </div>
  )
}
